import React, { useEffect, useState } from "react";
import { Icon } from "@blueprintjs/core";
import muesliAImg from "./assets/MuesliA.png";
import muesliBImg from "./assets/MuesliB_D.png";
import DescImg from "./assets/Desc.png";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles } from "../../../../components/colors";
import { nudge } from "../../../../translations/experiments/Nudge";

type MuesliScreenType = {
    title: string;
    description: string;
    optionA: string;
    optionB: string;
    features: string[];
    allergens: string;
    nutritionTable: { [key: string]: string };
    ingredients: { [key: string]: string };
};

export type NudgeChoice = 0 | 1;

export interface SelectionData {
    choice: number;
    millisecondsSinceFirstRender: number;
}

export interface NudgingScreenResult {
    selections: SelectionData[];
}

export const NudgingScreen2InitialResult: NudgingScreenResult = {
    selections: []
};

function NutritionFactsTable({ nutritionTable }: { nutritionTable: { [key: string]: string } }) {
    return (
        <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            margin: '10px 0 12px 0',
            background: '#f8f9fa',
            borderRadius: 8,
            overflow: 'hidden'
        }}>
            <tbody>
            {Object.entries(nutritionTable).map(([name, value]) => (
                <tr key={name} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td style={{
                        padding: '6px 10px',
                        fontWeight: 500,
                        color: '#456',
                        textAlign: 'left',
                        minWidth: 140
                    }}>{name}</td>
                    <td style={{
                        padding: '6px 10px',
                        color: '#234',
                        textAlign: 'right'
                    }}>{value}</td>
                </tr>
            ))}
            </tbody>
        </table>
    );
}

function MuesliOptionCard({
                              image,
                              title,
                              selected,
                              onClick,
                              translation
                          }: {
    image: string,
    title: string,
    selected: boolean,
    onClick: () => void,
    translation: MuesliScreenType
}) {
    return (
        <div
            style={selected ? commonStyles.optionCard.selected : commonStyles.optionCard.default}
            onClick={onClick}
        >
            {selected && (
                <div style={commonStyles.selectionCheck}>
                    <Icon icon="tick" size={16} />
                </div>
            )}

            <div style={{
                marginBottom: 14,
                borderRadius: 12,
                overflow: "hidden",
                boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)"
            }}>
                <img
                    src={image}
                    alt={title}
                    style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: 320,
                        objectFit: "contain",
                        display: "block",
                        padding: "24px 10px",
                        background: "#f8f9fa"
                    }}
                />
            </div>
            <h3 style={{
                color: colors.brillantBlue,
                marginBottom: 10,
                fontSize: 18,
                fontWeight: 600
            }}>
                {title}
            </h3>
            <div style={{ fontSize: 13, margin: "7px 0", color: "#456" }}>
                <b>Zutaten:</b> {Object.values(translation.ingredients).join(", ")}
            </div>
            <NutritionFactsTable nutritionTable={translation.nutritionTable} />
            {/* Desc.png image below the table, above the allergens */}
            <div style={{ width: "100%", textAlign: "center", margin: "18px 0 10px 0" }}>
                <img
                    src={DescImg}
                    alt="Beschreibung"
                    style={{
                        width: "100%",
                        maxWidth: 260,
                        height: "auto",
                        display: "block",
                        margin: "0 auto"
                    }}
                />
            </div>
            <div style={{ fontSize: 13, margin: "7px 0", color: "#a23" }}>
                <b>Allergene:</b> {translation.allergens}
            </div>
        </div>
    );
}

export function NudgingScreen2(props: ScreenProps) {
    const { language } = useLanguage();
    const translation = nudge[language]?.screens?.muesliBad as MuesliScreenType;
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [selected, setSelected] = useState<number | null>(null);
    const { setCanContinue, setResult } = props;

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
        setCanContinue(false);
        setResult(() => ({ ...NudgingScreen2InitialResult }));
    }, [setCanContinue, setResult]);

    useEffect(() => {
        setCanContinue(selected !== null);
        if (selected !== null) {
            setResult((prev: NudgingScreenResult) => {
                return {
                    ...prev,
                    selections: [
                        {
                            choice: selected,
                            millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp
                        }
                    ]
                };
            });
        }
    }, [selected, setCanContinue, setResult, firstRenderTimestamp]);

    // Dedicated question
    const chooseQuestion = language === "en"
        ? "Which muesli do you choose?"
        : "Für welches Müsli entscheidest du dich?";

    return (
        <div className="experiment-container" style={{
            maxWidth: 1250,
            margin: "0 auto",
            padding: "24px 20px 44px",
            backgroundColor: colors.backgroundLight
        }}>
            <div style={{
                background: colors.white,
                borderRadius: 14,
                boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
                padding: "24px 40px 28px",
                marginBottom: 28
            }}>
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {translation.title}
                </h2>
                <div style={{
                    fontSize: 17,
                    lineHeight: 1.7,
                    color: colors.textMedium,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}>
                    <p style={{ marginTop: 0, marginBottom: 20 }}>
                        {translation.description}
                    </p>
                </div>
            </div>

            {/* Dedicated question above the cards */}
            <div style={{ textAlign: "center", margin: "0 0 28px 0" }}>
                <span style={{
                    color: colors.brillantBlue,
                    fontSize: 22,
                    fontWeight: 600,
                    textAlign: "center"
                }}>{chooseQuestion}</span>
            </div>

            <div style={{
                display: "flex",
                flexDirection: window.innerWidth < 600 ? "column" : "row",
                gap: 24,
                justifyContent: "center",
                alignItems: "flex-start"
            }}>
                <MuesliOptionCard
                    image={muesliAImg}
                    title={translation.optionA}
                    selected={selected === 0}
                    onClick={() => setSelected(0)}
                    translation={translation}
                />
                <MuesliOptionCard
                    image={muesliBImg}
                    title={translation.optionB}
                    selected={selected === 1}
                    onClick={() => setSelected(1)}
                    translation={translation}
                />
            </div>

            {selected !== null && (
                <div style={{
                    textAlign: "center",
                    marginTop: 28,
                    padding: "12px 20px",
                    fontSize: 18,
                    fontWeight: 500,
                    color: colors.brillantBlue,
                    backgroundColor: colors.lichtBlauLighter,
                    borderRadius: 8,
                    display: "inline-block",
                    marginLeft: "auto",
                    marginRight: "auto"
                }}>
                    <Icon icon="tick-circle" style={{ marginRight: 8 }} />
                    {selected === 0
                        ? translation.optionA
                        : translation.optionB
                    }
                </div>
            )}
        </div>
    );
}

export const NudgingScreen2Config: ScreenConfig = {
    component: NudgingScreen2,
    defaultResult: NudgingScreen2InitialResult,
    canContinueDefault: false
};